

import React from "react";
import "./AudiobookFlipBook.css";
import AnimatedStoryText from "./AnimatedStoryText";
import AnimatedStoryImage from "./AnimatedStoryImage";

const PAPER = "#FFFFFF";
const TEXT_NAVY = "#23314E";
const ACCENT_BLUE = "#3B82F6";
const MUTED = "#8A96A8";

const renderDesktopStoryText = (text, withDropCap) => {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const sentences = trimmed.split(/(?<=[.!?])\s+/).filter(Boolean);
  const first = sentences[0] || trimmed;
  const restSentences = sentences.slice(1);

  return (
    <div className="storybook-text-block">
      {withDropCap ? (
        <p className="storybook-line storybook-line--first">
          <span className="storybook-drop-cap">{first.charAt(0)}</span>
          {first.slice(1)}
        </p>
      ) : (
        <p className="storybook-line storybook-line--first">{first}</p>
      )}
      {restSentences.length > 0 && (
        <>
          <div className="storybook-divider" aria-hidden="true">
            <span className="storybook-divider-line" />
            <span className="storybook-heart">♥</span>
            <span className="storybook-divider-line" />
          </div>
          <p className="storybook-line">{restSentences.join(" ")}</p>
        </>
      )}
    </div>
  );
};


const AudiobookSinglePage = ({
  page,
  pageNumber,
  totalPages,
  side,
  isPlaying,
  onTogglePlayPause,
  hideReadButton = false,
  bookTitle = "",
  animateStoryText = false,
  storyTextAnimationSpeed = 80,
  waitForNarration = false,
  narrationActive = false,
  animateStoryImage = false,
  onShareBook = null,
  onReadAgain = null,
  onCreateOwn = null,
}) => {
  if (!page) return null;

  // ── Celebration end page (mirrors the iOS post-completion prompt):
  // confetti burst + encouragement + share/read-again/create options.
  if (page.type === "generating") {
    return (
      <div
        className="storybook-page storybook-page--end"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 10,
          padding: 24,
          height: "100%",
          background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        }}
      >
        <div style={{ width: 150, height: 150, marginBottom: 4, overflow: "hidden" }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            aria-label="Pip writing the next pages"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          >
            <source src="/assets/pixley-drawing-only.mov" type='video/mp4; codecs="hvc1"' />
            <source src="/assets/pixley-drawing-only.webm" type="video/webm" />
            <source src="/assets/panda-jumping.mp4" type="video/mp4" />
          </video>
        </div>
        <span
          style={{
            fontFamily: "'Fredoka', sans-serif",
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "#0065F8",
          }}
        >
          Still being written
        </span>
        <h2
          style={{
            fontFamily: "'Fredoka', sans-serif",
            fontSize: "clamp(26px, 3.5vw, 38px)",
            fontWeight: 700,
            color: "#0B1A33",
            margin: 0,
            lineHeight: 1.1,
          }}
        >
          The next pages are on their way…
        </h2>
        <p style={{ fontSize: 15, color: "#475569", margin: "2px 0 10px", maxWidth: 330, fontFamily: "'Fredoka', sans-serif" }}>
          Keep reading — new pages appear here as soon as they're painted!
        </p>
        <div
          style={{
            width: 190,
            height: 9,
            borderRadius: 999,
            background: "rgba(0,101,248,0.12)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "40%",
              borderRadius: 999,
              background: "linear-gradient(90deg, #0065F8, #00CAFF)",
              animation: "storybook-generating-slide 1.4s ease-in-out infinite",
            }}
          />
        </div>
        <style>{`@keyframes storybook-generating-slide { 0% { transform: translateX(-110%); } 100% { transform: translateX(310%); } }`}</style>
      </div>
    );
  }

  if (page.type === "end") {
    const CONFETTI_COLORS = ["#0065F8", "#FF6B9A", "#FFC93C", "#37D67A", "#8A5CF6", "#00CAFF"];
    const confetti = Array.from({ length: 28 }, (_, i) => ({
      left: `${(i * 37) % 100}%`,
      delay: `${(i % 10) * 0.28}s`,
      duration: `${2.6 + (i % 5) * 0.45}s`,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      size: 7 + (i % 4) * 3,
      rotate: (i * 47) % 360,
    }));
    const endBtn = (bg, shadow) => ({
      fontFamily: "'Fredoka', sans-serif",
      fontWeight: 700,
      fontSize: 15,
      color: "#FFFFFF",
      background: bg,
      border: "none",
      borderRadius: 14,
      padding: "13px 26px",
      boxShadow: `0px 4px 0px ${shadow}`,
      cursor: "pointer",
      width: "100%",
      maxWidth: 260,
    });
    return (
      <div
        className="relative overflow-hidden flex flex-col items-center justify-center text-center"
        style={{
          width: "min(46vw, 560px)",
          aspectRatio: "1 / 1.28",
          background:
            "radial-gradient(120% 90% at 50% 0%, #FFF7E6 0%, #F5FAFF 42%, #DCEBFF 100%)",
          borderRadius: "inherit",
        }}
      >
        <style>{`
          @keyframes storybook-confetti-fall {
            /* Animate top (container-relative) — translateY(%) is relative to
               the piece's own few-pixel height and goes nowhere visible. */
            0% { top: -8%; transform: rotate(0deg); opacity: 1; }
            85% { opacity: 1; }
            100% { top: 108%; transform: rotate(540deg); opacity: 0; }
          }
        `}</style>
        {confetti.map((c, i) => (
          <span
            key={i}
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "-8%",
              left: c.left,
              width: c.size,
              height: c.size * 0.45,
              background: c.color,
              borderRadius: 2,
              transform: `rotate(${c.rotate}deg)`,
              animation: `storybook-confetti-fall ${c.duration} linear ${c.delay} infinite`,
              pointerEvents: "none",
            }}
          />
        ))}

        <style>{`
          @keyframes storybook-end-bounce {
            0%, 100% { transform: translateY(0) rotate(-3deg); }
            50% { transform: translateY(-10px) rotate(3deg); }
          }
        `}</style>
        <div className="relative z-10 px-8 flex flex-col items-center gap-3">
          {/* Pip celebrating */}
          <div
            style={{
              width: 210,
              height: 210,
              borderRadius: "50%",
              background: "radial-gradient(circle at 30% 25%, #FFFFFF 0%, #DCEBFF 100%)",
              border: "3px solid rgba(0,101,248,0.18)",
              boxShadow: "0px 8px 24px rgba(0,101,248,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 4,
              overflow: "hidden",
            }}
          >
            {/* Celebration animation of the mascot (from the panda_vids
                bucket, transcoded small). muted+playsInline keeps mobile
                autoplay allowed. */}
            <video
              autoPlay
              loop
              muted
              playsInline
              aria-label="Pip celebrating"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            >
              {/* Transparent pair (LandingPage pattern): HEVC-alpha for
                  Safari, VP9-alpha webm for Chrome/Firefox/Edge, opaque mp4
                  as the last-resort fallback. */}
              <source src="/assets/panda-jumping-alpha.mov" type='video/mp4; codecs="hvc1"' />
              <source src="/assets/panda-jumping.webm" type="video/webm" />
              <source src="/assets/panda-jumping.mp4" type="video/mp4" />
            </video>
          </div>
          <span
            style={{
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#0065F8",
            }}
          >
            You finished the book
          </span>
          <h2
            style={{
              fontFamily: "'Fredoka', sans-serif",
              fontSize: "clamp(30px, 4vw, 44px)",
              fontWeight: 700,
              color: "#0B1A33",
              margin: 0,
              lineHeight: 1.1,
            }}
          >
            The End!
          </h2>
          <p style={{ fontSize: 15, color: "#475569", margin: "2px 0 14px", maxWidth: 330 }}>
            What a story. Someone you know would love this one — share it with them!
          </p>

          {onShareBook && (
            <button onClick={onShareBook} style={endBtn("#0065F8", "#073B8E")}>
              Share this storybook
            </button>
          )}
          {onReadAgain && (
            <button onClick={onReadAgain} style={endBtn("#FF6B9A", "#C94D73")}>
              Read it again
            </button>
          )}
          {onCreateOwn && (
            <button
              onClick={onCreateOwn}
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                color: "#0065F8",
                background: "#FFFFFF",
                border: "2px solid rgba(0,101,248,0.25)",
                borderRadius: 14,
                padding: "11px 24px",
                boxShadow: "0px 3px 0px rgba(11,26,51,0.08)",
                cursor: "pointer",
                width: "100%",
                maxWidth: 260,
              }}
            >
              Make your own storybook
            </button>
          )}
        </div>
      </div>
    );
  }

  if (page.type === "contain" || side === "cover") {
    // Full-bleed cover: the art fills the whole 2:3 book face (new covers are
    // generated 2:3 so they fit exactly; legacy 1:1 covers center-crop the
    // sides via object-cover) with the
    // title + creator overlaid on a legibility gradient, like a real book jacket.
    // The explicit width + aspect ratio give the cover its own size — the
    // wrapper (.storybook-pages-wrap--cover) is a flex row whose sheet shrinks
    // to content, and absolutely-positioned children contribute no size, so
    // percentage/auto sizing here collapses to a sliver.
    return (
      // Outer stays overflow-visible so Read to me can sit to the right of the cover
      // without being clipped; art/title stay inside an overflow-hidden face.
      <div
        className="relative"
        style={{
          background: PAPER,
          aspectRatio: "2 / 3",
          // Third term keeps the whole book visible on short viewports:
          // 152px ≈ nav + top gap + bottom breathing room, ×(2/3) converts
          // the available height back to width at the 2:3 ratio.
          width: "min(520px, 88vw, calc((100vh - 152px) * 2 / 3))",
        }}
      >
        <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
          {page.image_url && (
            <img
              src={page.image_url}
              alt="Cover"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.target.src = "/assets/panda-filled.png"; }}
            />
          )}

          {/* Spine: dark crease + highlight ridge on the left edge so the
              full-bleed jacket still reads as a physical hardcover */}
          <div
            className="absolute inset-y-0 left-0 pointer-events-none z-10"
            style={{
              width: 30,
              background:
                "linear-gradient(90deg, rgba(15,25,45,0.42) 0%, rgba(15,25,45,0.2) 30%, rgba(255,255,255,0.14) 52%, rgba(15,25,45,0.12) 72%, rgba(15,25,45,0) 100%)",
            }}
          />


          {/* Bottom legibility gradient behind the title block. Restored
              2026-08-20: the backend cover prompt says "No text" (AI-rendered
              titles get misspelled), so the client overlay is the ONLY title
              a cover has. */}
          <div
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{
              height: "52%",
              background: "linear-gradient(180deg, rgba(15,25,45,0) 0%, rgba(15,25,45,0.42) 45%, rgba(15,25,45,0.78) 100%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-0 px-6 pb-7 text-center">
            {page.text && (
              <h1
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  color: "#FFFFFF",
                  textShadow: "0 2px 12px rgba(10,20,40,0.55)",
                  lineHeight: 1.12,
                }}
              >
                {page.text}
              </h1>
            )}
            {page.creator && (
              <div
                className="inline-flex items-center gap-2 mt-1"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  background: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.28)",
                  borderRadius: 999,
                  padding: "6px 14px",
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="#FFD68A" aria-hidden="true">
                  <path d="M12 2l1.6 5.8L19.5 9.5l-5.9 1.7L12 17l-1.6-5.8L4.5 9.5l5.9-1.7L12 2z" />
                </svg>
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 500 }}>
                  A story by <span style={{ color: "#FFFFFF", fontWeight: 700 }}>{page.creator}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {onTogglePlayPause && !hideReadButton && (
          <div
            className="absolute top-4 z-10"
            style={{ left: "100%", marginLeft: 14 }}
          >
            <button
              type="button"
              onClick={onTogglePlayPause}
              className="storybook-read-btn storybook-read-btn--cover"
              style={{ position: "static" }}
              aria-label={isPlaying ? "Pause" : "Read to me"}
            >
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
              </svg>
              <span>{isPlaying ? "Pause" : "Read to me"}</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    // Transparent: the page-face behind supplies the paper (white + grain);
    // painting a solid fill here would read as an inset box on the page.
    <div className="w-full h-full relative flex flex-col">
      {side === "mobile" && page.type === "voice_intro" ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3 min-h-[60svh]">
          <img
            src="/assets/pixley-panda-nobg.png"
            alt=""
            aria-hidden="true"
            style={{ width: 72, height: 72, objectFit: "contain" }}
          />
          <p className="text-xl font-bold" style={{ color: TEXT_NAVY, fontFamily: "'Fredoka', sans-serif" }}>
            Let's create your story together!
          </p>
          <p className="text-sm leading-relaxed" style={{ color: MUTED, fontFamily: "'Fredoka', sans-serif", maxWidth: 280 }}>
            When you're ready, press the button below and start talking.
          </p>
        </div>
      ) : side === "mobile" ? (
        <>
          <button
            type="button"
            onClick={onTogglePlayPause}
            className="absolute top-2 right-2 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5 z-20 text-xs"
            style={{
              background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
              fontFamily: "'Fredoka', sans-serif",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.4), 0 3px 0 #073B8E, 0 8px 18px rgba(0,101,248,0.28)",
              textShadow: "0 1px 0 rgba(9,46,125,0.35)",
              fontWeight: 700,
              letterSpacing: "0.01em",
            }}
          >
            {isPlaying ? "Pause" : "Read to me"}
          </button>
          {animateStoryImage ? (
            <div className="flex items-center justify-center h-[42svh] mb-4">
              <div className="w-full h-full max-h-[42svh] rounded-2xl overflow-hidden">
                <AnimatedStoryImage
                  key={`mobile-img-${pageNumber}`}
                  src={page.image_url || null}
                  alt={`Page ${pageNumber}`}
                  objectFit="contain"
                  enableAnimation
                />
              </div>
            </div>
          ) : page.image_url ? (
            <div className="flex items-center justify-center h-[42svh] mb-4">
              <img src={page.image_url} alt={`Page ${pageNumber}`} className="max-h-full max-w-full object-contain" onError={(e) => { e.target.src = "/assets/panda-filled.png"; }} />
            </div>
          ) : null}
          {page.text && (
            <div className="flex-1 overflow-y-auto px-4 pt-2">
              {animateStoryText ? (
                <AnimatedStoryText
                  text={page.text}
                  animationSpeed={storyTextAnimationSpeed}
                  enableAnimation
                  variant="plain"
                  waitForNarration={waitForNarration}
                  narrationActive={narrationActive}
                  style={{ color: TEXT_NAVY, fontFamily: "Georgia, serif", fontSize: 17, lineHeight: 1.7 }}
                />
              ) : (
                <p className="font-serif text-[17px] leading-[1.7]" style={{ color: TEXT_NAVY }}>{page.text}</p>
              )}
            </div>
          )}
          <div className="mt-3 mb-4 text-center text-xs font-medium" style={{ color: ACCENT_BLUE, fontFamily: "'Fredoka', sans-serif" }}>
            Page {pageNumber} of {totalPages}
          </div>
        </>
      ) : side === "left" && page.type === "voice_intro" ? (
        <>
          <div className="storybook-bookmark-ribbon" aria-hidden="true" />
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3">
            <img
              src="/assets/pixley-panda-nobg.png"
              alt=""
              aria-hidden="true"
              style={{ width: 80, height: 80, objectFit: "contain" }}
            />
            <p
              className="text-xl font-bold"
              style={{ color: TEXT_NAVY, fontFamily: "'Fredoka', sans-serif" }}
            >
              Let's create your story together!
            </p>
            <p className="text-sm leading-relaxed" style={{ color: MUTED, fontFamily: "'Fredoka', sans-serif", maxWidth: 280 }}>
              When you're ready, press the button below and start talking.
            </p>
          </div>
        </>
      ) : side === "left" ? (
        <>
          <div className="storybook-bookmark-ribbon" aria-hidden="true" />

          {!hideReadButton && (
            <button type="button" onClick={onTogglePlayPause} className="storybook-read-btn" aria-label={isPlaying ? "Pause" : "Read to me"}>
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
              </svg>
              <span>{isPlaying ? "Pause" : "Read to me"}</span>
            </button>
          )}

          {page.text && (
            // Typography (1.1rem/1.6 in a 420px column) is sized so the
            // longest generated page (~120 words, reading level 5) fits with
            // room to spare — one consistent size, no scrolling, no clipping
            <div className="flex-1 flex px-2 py-2 overflow-hidden">
              {animateStoryText ? (
                <AnimatedStoryText
                  text={page.text}
                  animationSpeed={storyTextAnimationSpeed}
                  enableAnimation
                  withDropCap={pageNumber === 1}
                  variant="storybook"
                  waitForNarration={waitForNarration}
                  narrationActive={narrationActive}
                />
              ) : (
                renderDesktopStoryText(page.text, pageNumber === 1)
              )}
            </div>
          )}

          <div className="storybook-page-number">Page {pageNumber} of {totalPages}</div>
        </>
      ) : animateStoryImage ? (
        <div className="storybook-image-frame">
          <AnimatedStoryImage
            key={`page-img-${pageNumber}`}
            src={page.image_url || null}
            alt={`Page ${pageNumber}`}
            objectFit="contain"
            enableAnimation
          />
        </div>
      ) : page.image_url ? (
        <div className="storybook-image-frame">
          <img src={page.image_url} alt={`Page ${pageNumber}`} onError={(e) => { e.target.src = "/assets/panda-filled.png"; }} />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
          <svg
            width="72"
            height="72"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ opacity: 0.22 }}
          >
            <path
              d="M32 14v36M32 14c0 0-3.5-3.5-12-3.5S8 14 8 14v36s3.5-3.5 12-3.5S32 50 32 50M32 14c0 0 3.5-3.5 12-3.5S56 14 56 14v36s-3.5-3.5-12-3.5S32 50 32 50"
              stroke="#0B1A33"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

export default AudiobookSinglePage;
